import { useValidate } from "formvalidation/hooks";
import React, { useEffect } from "react";
import { Input, Message } from "semantic-ui-react";
import { useCustomQuery, useSemanticUiInputRef } from "utilities/hookutils";
import { saveOrCancel } from "utilities/inputUtils";

interface BookkeepingCodeInputProps {
  validatorKey: string
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  onEnter?: () => void
  onEsc?: () => void
  focusOnMount?: boolean
}

export const BookkeepingCodeInput = (props: BookkeepingCodeInputProps) => {
  const inputRef = useSemanticUiInputRef();

  useEffect(() => {
    if (props.focusOnMount) {
      inputRef.ref.current.focus();
    }
  }, []);

  const onChange = (value: string) => {
    // The code only allows upper case letters and numbers. We want to change any lowercase
    //  letter to uppercase and not allow other characters.
    const newCode = value.toLocaleUpperCase().replace(/[^A-Z0-9]/g, "");
    props.onChange(newCode);
  };

  const bookkeepingCodeValidator = useCustomQuery({
    queryKey: ["bookkeepingCodeValidator", props.value],
    queryFn: () => window.CoreApi.Bookkeeping.CodeExists({ code: props.value }).then(r => r.data),
    enabled: false,
  });

  const validateCode = async (): Promise<string | boolean> => {
    if (props.value.length !== 3) {
      return RESX.GeneralWarnings.HasToBeAtLeastXCharacters.replace("{0}", "3");
    }

    const codeExists = await bookkeepingCodeValidator.refetch();
    if (codeExists.data.data) {
      return RESX.Bookkeeping.ABookkeepingWithThisCode;
    }

    return true;
  }

  const validation = useValidate({
    key: props.validatorKey,
    title: RESX.Bookkeeping.Code,
    value: props.value,
    validate: validateCode,
    ref: inputRef.ref,
    type: "manual"
  });

  const onBlur = async () => {
    props.onBlur?.();
  };

  return <>
    <Input
      ref={inputRef.refSetter}
      placeholder={RESX.Bookkeeping.BookkeepingCodeDesc}
      maxLength={3}
      fluid
      value={props.value}
      onChange={(_, { value }) => { onChange(value); }}
      error={validation.error.hasError}
      loading={bookkeepingCodeValidator.isFetching}
      onBlur={onBlur}
      onKeyDown={e => saveOrCancel(e, { onEnter: props.onEnter, onEsc: props.onEsc, validation, value: props.value })}
    />
    {validation.error.hasError && <Message color="red">{validation.error.errorMessage}</Message>}
  </>
}